import {Matrix, MatrixUtil, Point, PointUtil, Rectangle, RectangleUtil} from "../geom";
import { RenderScene } from "../scene";
import {GLRenderer} from "../glrenderer";
import {ElementType, HitTestInfo, SelectionBoxHit, StitchElement, SelectionDataElemUtil, SelectionBox} from "../elements";
import {SelectionData, SelectionDataUtil, UpdateType} from "./SelectionState";

export enum ViewPortUpdateReason {
    Refresh = "Refresh",
    Pan = "Pan",
    Zoom = "Zoom",
}
export type ViewPortUpdateFn = (r: Rectangle, canvasDimensions: Point, reason: ViewPortUpdateReason) => void;

/**
 * Interface that gets called after canvas created so can attach mouse events.
 */
export interface IMouseGestureEventHandler {
    /** Called after canvas and renderer created so can register any mouse events to canvas */
    registerEvents(canvas: HTMLElement, renderer: GLRenderer, enableResizeObserver?: boolean): () => void;
}

export class CustomHandler {
    public shouldStartLeftMouseDrag(pt1: Point): boolean {
        return false;
    }
    public shouldStartTouchDrag(pt1: Point): boolean {
        return false;
    }
    public handleClick(pt1: Point): boolean { return false; }
    public handleTouchSelect(pt1: Point): boolean { return false; }
    public doMouseDrag(pt1: Point, pt2: Point): boolean { return false; }
    public doTouchDrag(pt1: Point, pt2: Point): boolean { return false;}
    public doMouseWheel(pt: Point, deltaY: number) { return false; }
    public endMouseDrag(): void {}
    public endTouchDrag(): void {}
    public shouldStartPinchZoom(pt1: Point, pt2: Point): boolean { return false; }
    public doPinchZoom(ptOrg1: Point, ptOrg2: Point, ptNew1: Point, ptNew2: Point): boolean { return false; }
    public endPinchZoom(): void {}
    public resetState(): void {}
}
export class MouseGestureHandler {
    public mouseDown(mouseEvent: MouseEvent): void {
        this.resetState();
        if (mouseEvent.button == 0) {
            this.mouseHitPt = {x: mouseEvent.offsetX, y: mouseEvent.offsetY};
            for (let h of this.handlers) {
                if (h.shouldStartLeftMouseDrag(this.mouseHitPt)) {
                    this.currMouseDrag = h;
                    break;
                }
            }
            if (this.currMouseDrag) {
                this.dragging = false;
                this.mouseHit = true;
            }
        }
        mouseEvent.preventDefault();
    }
    public mouseMove(mouseEvent: MouseEvent): void {
        if (this.dragging && mouseEvent.buttons == 0) {
            this.resetState();
        }
        if (this.currMouseDrag) {
            let pt = {x: mouseEvent.offsetX, y: mouseEvent.offsetY};
            let drag = this.currMouseDrag.doMouseDrag(this.mouseHitPt, pt);
            if (drag) {
                this.dragging = true;
            }
        }
        mouseEvent.preventDefault();
    }
    public mouseUp(mouseEvent: MouseEvent): void {
        if (!this.dragging) {
            let pt = {x: mouseEvent.offsetX, y: mouseEvent.offsetY};
            for (let h of this.handlers) {
                if (h.handleClick(pt)) {
                    break;
                }
            }
        }
        this.resetState();
        mouseEvent.preventDefault();
    }
    public mouseWheel(wheelEvent: WheelEvent): void {
        this.resetState();
        for (let h of this.handlers) {
            let pt = {x: wheelEvent.offsetX, y: wheelEvent.offsetY};
            if (h.doMouseWheel(pt, wheelEvent.deltaY)) {
                wheelEvent.preventDefault();
                break;
            }
        }
    }
    private initTouchZoom(touchEvent: TouchEvent): boolean {
        let ret = false;
        let r = (touchEvent.target as HTMLCanvasElement).getBoundingClientRect();
        let p1 = {x: touchEvent.targetTouches[0].clientX - r.left, y: touchEvent.targetTouches[0].clientY - r.top};
        let p2 = {x: touchEvent.targetTouches[1].clientX - r.left, y: touchEvent.targetTouches[1].clientY - r.top};

        if (this.currTouchDrag) {
            this.resetState();
        }
        for (let h of this.handlers) {
            if (h.shouldStartPinchZoom(p1, p2)) {
                this.touchHitPt = p1;
                this.touchHitPt2 = p2;
                this.currTouchPinch = h;
                ret = true;
                break;
            }
        }
        console.log("Zoom Touch Start");
        return ret;
    }
    private calcTouchMove(touchEvent: TouchEvent): void {
        if (touchEvent.targetTouches.length > 0 && this.currTouchDrag) {
            let p = this.touchHitPt;
            let r = (touchEvent.target as HTMLCanvasElement).getBoundingClientRect();
            let dragHitPt = {x: touchEvent.targetTouches[0].clientX - r.left, y: touchEvent.targetTouches[0].clientY - r.top};

            let p2 = {x: dragHitPt.x, y: dragHitPt.y};
            let drag = this.currTouchDrag.doTouchDrag(p, p2);
            if (drag) {
                this.dragging = true;
            }
        }
    }
    private calcTouchZoom(touchEvent: TouchEvent): void {
        if (touchEvent.targetTouches.length == 2 && this.currTouchPinch) {
            let r = (touchEvent.target as HTMLCanvasElement).getBoundingClientRect();
            let t1: Touch = touchEvent.targetTouches[0];
            let t2: Touch = touchEvent.targetTouches[1];
            if (t1 && t2) {
                let p1 = {x: t1.clientX - r.left, y: t1.clientY - r.top};
                let p2 = {x: t2.clientX - r.left, y: t2.clientY - r.top};
                let drag = this.currTouchPinch.doPinchZoom(this.touchHitPt, this.touchHitPt2, p1, p2);
                if (drag) {
                    this.dragging = drag;
                }
            }
        }
    }
    public touchStart(touchEvent: TouchEvent): void {
        this.resetState();
        if (touchEvent.targetTouches.length == 1) {
            let r = (touchEvent.target as HTMLCanvasElement).getBoundingClientRect();
            this.touchHitPt = {x: touchEvent.targetTouches[0].clientX - r.left, y: touchEvent.targetTouches[0].clientY - r.top};
            for (let h of this.handlers) {
                if (h.shouldStartTouchDrag(this.touchHitPt)) {
                    this.currTouchDrag = h;
                    break;
                }
            }
            if (!this.currTouchDrag) {
                for (let h of this.handlers) {
                    if (h.handleTouchSelect(this.touchHitPt)) {
                        break;
                    }
                }
            }
            console.log("Move Touch Start");
        } else if (touchEvent.targetTouches.length == 2) {
            this.initTouchZoom(touchEvent);
        }
        touchEvent.preventDefault();
    }
    public touchEnd(touchEvent: TouchEvent): void {
        this.resetState();
        touchEvent.preventDefault();
    }
    public touchMove(touchEvent: TouchEvent): void {
        if (this.currTouchDrag && touchEvent.targetTouches.length > 1) {
            this.initTouchZoom(touchEvent);
        }
        if (this.currTouchDrag) {
            this.calcTouchMove(touchEvent);
        }
        if (this.currTouchPinch) {
            this.calcTouchZoom(touchEvent);
        }
        touchEvent.preventDefault();
    }
    public mouseEnter(mouseEvent: MouseEvent): void {
    }
    public mouseLeave(mouseEvent: MouseEvent): void {
    }
    public resetState() {
        if (this.currMouseDrag) {
            this.currMouseDrag.endMouseDrag();
        }
        if (this.currTouchDrag) {
            this.currTouchDrag.endTouchDrag();
        }
        if (this.currTouchPinch) {
            this.currTouchPinch.endPinchZoom();
        }
        this.currMouseDrag = undefined;
        this.currTouchDrag = undefined;
        this.currTouchPinch = undefined;
        this.dragging = false;
        this.mouseHit = false;
        for (let h of this.handlers) {
            h.resetState();
        }
    }
    public isDragging(): boolean {
        return this.dragging;
    }
    protected dragging: boolean = false;
    protected mouseHit: boolean = false;
    protected mouseHitPt: Point = {x: 0, y: 0};
    protected touchHitPt: Point = {x: 0, y: 0};
    protected touchHitPt2: Point = {x: 0, y: 0};
    public handlers: Array<CustomHandler> = [];
    protected currMouseDrag?: CustomHandler;
    protected currTouchDrag?: CustomHandler;
    protected currTouchPinch?: CustomHandler;
}
/// data for viewport
export class ViewPortData {
    public constructor(viewPortUpdate?: ViewPortUpdateFn) {
        this.viewPortUpdater = viewPortUpdate;
    }

    public setViewPortUpdateFn(viewPortUpdate?: ViewPortUpdateFn) {
        let last = this.viewPortUpdater;
        this.viewPortUpdater = viewPortUpdate;
        if (!last) {
            this.updateViewport(ViewPortUpdateReason.Refresh);
        }
    }

    /// calculates viewport from canvas size/dpi/zoom/origin and assigns to viewPort variable
    public calcViewPort() {
        let widthp = this.cx / this.dpi * 254 / this.zoom;
        let heightp = this.cy / this.dpi * 254 / this.zoom;
        this.viewPort = {llx: this.origin.x - .5 * widthp, lly: this.origin.y - .5 * heightp, urx: this.origin.x + .5 * widthp, ury: this.origin.y + .5 * heightp};
    }

    /// calls calculateViewPort followeed by viewPortUpdate callback which was passed to constructor
    public updateViewport(reason: ViewPortUpdateReason) {
        this.calcViewPort();
        if (this.viewPortUpdater) {
            this.viewPortUpdater(this.viewPort, {x: this.cx, y: this.cy}, reason);
        }
    }

    /// converts canvas pixel value to embroidery point
    public clientToLogical(p: Point): Point {
        let x = p.x / this.cx * (this.viewPort.urx - this.viewPort.llx) + this.viewPort.llx;
        let y = this.viewPort.ury - p.y / this.cy * (this.viewPort.ury - this.viewPort.lly);
        return {x: x, y: y}
    }

    /// converts embroidery point to canvas pixel value
    public logicalToClient(p: Point): Point {
        let px = (p.x - this.viewPort.llx) * this.cx / (this.viewPort.urx - this.viewPort.llx);
        let py = (this.viewPort.ury - p.y) * this.cy / (this.viewPort.ury - this.viewPort.lly);
        return {x: px, y: py};
    }

    /// change canvas size, sets canvasVariables then calls updateViewport
    public canvasSizeChanged(cx: number, cy: number): void {
        this.cx = cx;
        this.cy = cy;
        this.updateViewport(ViewPortUpdateReason.Refresh);
    }

    /// calculate points per pixel
    public pointsPerPixel(): number {
        return .5 * ((this.viewPort.urx - this.viewPort.llx) / this.cx + (this.viewPort.ury - this.viewPort.lly) / this.cy);
    }

    /// force view port to update
    public setViewport(viewPort: Rectangle): void {
        if (RectangleUtil.width(viewPort) != 0 && RectangleUtil.height(viewPort) != 0) {
            this.origin = RectangleUtil.center(viewPort);
            this.viewPort = viewPort;
            let z1 = this.cx / this.dpi * 254 / RectangleUtil.width(viewPort);
            let z2 = this.cy / this.dpi * 254 / RectangleUtil.height(viewPort);
            this.zoom = .5 * (z1 + z2);
        }
    }

    public zoom: number = 1;
    public origin: Point = PointUtil.emptyPoint();
    public viewPort: Rectangle = RectangleUtil.emptyRect();

    protected cx: number = 500;
    protected cy: number = 500;
    protected dpi: number = 96;

    protected viewPortUpdater?: ViewPortUpdateFn;
}

export enum CursorType {
    Default='Default',
    NESWScale='NESWScale',
    NWSEScale='NWSEScale',
    EWScale='EWScale',
    NSScale='NSScale',
    Grab='Grab',
    Grabbing='Grabbing',
    Select='Select',
    Move='Move',
    NERotate='NERotate',
    NWRotate='NWRotate',
    SERotate='SERotate',
    SWRotate='SWRotate',
}
export type CursorStyleUpdateFn = (cursor: CursorType, canvas: HTMLElement) => void;
export type SelectionBoxHitFn = (pt: Point) => SelectionBoxHit;
export type CurrSelectionBoxRectFn = () => SelectionBox;
export type ElementHitFn = (elem: StitchElement, pt: Point, thresholdInPts: number) => HitTestInfo;
export type ProjectChangeUpdateFn = (type: UpdateType, data: SelectionData) => void;

export class SelectionInfo {
    public minSelectPx: number = 10;
    public forceSelectPx: number = 3;
    private onChange?: ProjectChangeUpdateFn;
    private tempUpdateType: UpdateType = UpdateType.SCALE;
    private selectionData: SelectionData = SelectionDataUtil.createEmpty();
    private orgRect?: Rectangle;
    private hitTestFn?: ElementHitFn;
    private currSelectionRectFn?: CurrSelectionBoxRectFn;
    constructor(hitTestFn?: ElementHitFn, onChange?: ProjectChangeUpdateFn, currSelectionRectFn?: CurrSelectionBoxRectFn) {
        this.onChange = onChange;
        this.hitTestFn = hitTestFn;
        this.currSelectionRectFn = currSelectionRectFn;
    }
    public getOrgRect(): Rectangle | undefined {
        return this.orgRect;
    }
    public setProjectUpdateFn(onChange?: ProjectChangeUpdateFn) {
        this.onChange = onChange;
    }
    public setHitTestFn(hitTestFn?: ElementHitFn) {
        this.hitTestFn = hitTestFn;
    }
    public setCurrSelectionRectFn(currSelectionRectFn?: CurrSelectionBoxRectFn) {
        this.currSelectionRectFn = currSelectionRectFn;
    }
    public deselectAll(): void {
        if (this.selectionData.selectElems.length > 0) {
            let d = SelectionDataUtil.cloneSelectionData(this.selectionData);
            d.selectElems = [];
            d.dragging = false;
            this.orgRect = undefined;
            d.isRotating = false;
            if (this.onChange) {
                this.onChange(UpdateType.SELECT, d);
            }
        }
    }
    public hitTest(scene: RenderScene, p: Point, pointsPerPixel: number): number {
        let r = -1;
        if (scene.elements.length > 0) {
            let k = true;
            let m = this.minSelectPx * pointsPerPixel;
            let f = this.forceSelectPx * pointsPerPixel;
            let select = -1;
            for (let i=scene.elements.length-1;i>=0 && k;i--) {
                let e = scene.elements[i];
                if (e && e.type != ElementType.IMAGE && this.hitTestFn) {
                    let se = e as StitchElement;
                    let hi = this.hitTestFn(se, p, m);
                    if (hi.hit) {
                        select = i;
                        if (hi.distanceInPoints && hi.distanceInPoints < f) {
                            k = false;
                        }
                    }
                }
            }
            if (select >= 0) {
                return select;
            }
        }
        return r;
    }
    public setCurrSelection(d: SelectionData, callOnChange: boolean = false) {
        if (callOnChange && this.onChange) {
            this.onChange(UpdateType.SELECT, SelectionDataUtil.cloneSelectionData(d));
        } else {
            this.selectionData = d;
        }
    }
    public getCurrSelection(): SelectionData {
        return this.selectionData;
    }
    public calcSizeMatrix(p1: Point, p2: Point, hit: SelectionBoxHit, preserveAspect: boolean): Matrix {
        let dx: number = p2.x - p1.x;
        let dy: number = p2.y - p1.y;
        if (this.orgRect && !this.selectionData.isRotating) {
            let orgRect = this.orgRect;
            let newRect = orgRect;
            if (hit == SelectionBoxHit.hitTopLeft) {
                newRect = RectangleUtil.createRect(newRect.llx + dx, newRect.lly, newRect.urx, newRect.ury + dy);
            } else if (hit == SelectionBoxHit.hitTopRight) {
                newRect = RectangleUtil.createRect(newRect.llx, newRect.lly, newRect.urx + dx, newRect.ury + dy);
            } else if (hit == SelectionBoxHit.hitBottomLeft) {
                newRect = RectangleUtil.createRect(newRect.llx + dx, newRect.lly + dy, newRect.urx, newRect.ury);
            } else if (hit == SelectionBoxHit.hitBottomRight) {
                newRect = RectangleUtil.createRect(newRect.llx, newRect.lly + dy, newRect.urx + dx, newRect.ury);
            }
            if (preserveAspect && RectangleUtil.width(orgRect) != 0 && RectangleUtil.height(orgRect) != 0) {
                let xs = RectangleUtil.width(newRect) / RectangleUtil.width(orgRect);
                let ys = RectangleUtil.height(newRect) / RectangleUtil.height(orgRect);
                let s = xs;
                if (Math.abs(ys) > Math.abs(xs)) {
                    s = ys;
                }
                let w = RectangleUtil.width(orgRect) * s;
                let h = RectangleUtil.height(orgRect) * s;
                if (hit == SelectionBoxHit.hitTopLeft) {
                    newRect = RectangleUtil.createRect(newRect.urx - w, newRect.lly, newRect.urx, newRect.lly + h);
                } else if (hit == SelectionBoxHit.hitTopRight) {
                    newRect = RectangleUtil.createRect(newRect.llx, newRect.lly, newRect.llx + w, newRect.lly + h);
                } else if (hit == SelectionBoxHit.hitBottomLeft) {
                    newRect = RectangleUtil.createRect(newRect.urx - w, newRect.ury - h, newRect.urx, newRect.ury);
                } else if (hit == SelectionBoxHit.hitBottomRight) {
                    newRect = RectangleUtil.createRect(newRect.llx, newRect.ury - h, newRect.llx + w, newRect.ury);
                }
            }
            return MatrixUtil.scaleToRect(orgRect, newRect);
        } else if (this.orgRect && this.selectionData.isRotating) {
            let orgRect = this.orgRect;
            let ox = orgRect.llx;
            let oy = orgRect.lly;
            if (hit == SelectionBoxHit.hitTopLeft) {
                ox = orgRect.llx;
                oy = orgRect.ury;
            } else if (hit == SelectionBoxHit.hitTopRight) {
                ox = orgRect.urx;
                oy = orgRect.ury;
            } else if (hit == SelectionBoxHit.hitBottomLeft) {
                ox = orgRect.llx;
                oy = orgRect.lly;
            } else if (hit == SelectionBoxHit.hitBottomRight) {
                ox = orgRect.urx;
                oy = orgRect.lly;
            }
            let cx: number = RectangleUtil.center(orgRect).x;
            let cy: number = RectangleUtil.center(orgRect).y;
            let nx: number = ox + dx;
            let ny: number = oy + dy;

            let deltax1: number = ox - cx;
            let deltay1: number = oy - cy;
            let deltax2: number = nx - cx;
            let deltay2: number = ny - cy;
            let a1 = Math.atan2(deltay1, deltax1);
            let a2 = Math.atan2(deltay2, deltax2);
            let ang = a2 - a1;
            return MatrixUtil.rotateAroundPoint(ang, cx, cy);
        }
        return MatrixUtil.identityMatrix();
    }
    public updateTransform(m: Matrix, hit: SelectionBoxHit, type: UpdateType): void {
        let orgRect = this.orgRect;
        if (this.selectionData.selectElems.length > 0 && orgRect) {
            this.tempUpdateType = type;
            let d = SelectionDataUtil.cloneSelectionData(this.selectionData);
            d.dragging = true;
            if (d.isRotating) {
                d.dragTransformation = m;
                d.dragRect = orgRect;
            } else {
                let p1 = PointUtil.createPoint(orgRect.llx, orgRect.lly);
                let p2 = PointUtil.createPoint(orgRect.urx, orgRect.ury);
                p1 = MatrixUtil.calcPoint(m, p1);
                p2 = MatrixUtil.calcPoint(m, p2);
                d.dragTransformation = m;
                d.dragRect = RectangleUtil.createRect(p1.x, p1.y, p2.x, p2.y);
            }
            d.dragUpdateType = this.tempUpdateType;
            d.dragHit = hit;
            if (this.onChange) {
                this.onChange(this.tempUpdateType, d);
            }
        }
    }
    public beginOperation() {
        if (this.currSelectionRectFn) {
            this.orgRect = this.currSelectionRectFn().rect;
        }
    }
    public endOperation() {
        let d = SelectionDataUtil.cloneSelectionData(this.selectionData);
        d.dragging = false;
        d.dragRect = undefined;
        this.orgRect = undefined;
        if (this.onChange) {
            this.onChange(this.tempUpdateType, d);
        }
    }
    public hasSelection(): boolean {
        return this.selectionData.selectElems.length > 0;
    }
    public isRotation(): boolean {
        return this.selectionData.isRotating;
    }
    public toggleRotation(): void {
        let d = SelectionDataUtil.cloneSelectionData(this.selectionData);
        SelectionDataUtil.toggleRotation(d);
        if (this.onChange) {
            this.onChange(UpdateType.SELECT, d);
        }
    }
}

export const evaluateSelect = (p: Point, selectionInfo: SelectionInfo, scene: RenderScene | undefined,
                        viewPortData: ViewPortData,
                        calcSelectionBoxHitFn: (pt: Point) => SelectionBoxHit): boolean => {
    let testSelect = true;
    let deselectAll = false;
    let r = false;
    if (!scene || scene.elements.length == 0) {
        selectionInfo.deselectAll();
        r = selectionInfo.hasSelection();
        testSelect = false;
    } else if (selectionInfo.hasSelection()) {
        let h = calcSelectionBoxHitFn(p);
        if (h != SelectionBoxHit.hitNothing) {
            testSelect = false;
        }
    }
    if (testSelect && scene) {
        let h = selectionInfo.hitTest(scene, p, viewPortData.pointsPerPixel());
        if (h >= 0) {
            let d = SelectionDataUtil.createEmpty();
            let e = SelectionDataElemUtil.emptySelectionDataElem();
            e.elementIndex = h;
            let elem = scene.elements[h];
            if (elem && elem.type != ElementType.IMAGE) {
                let selem = elem as StitchElement;
                e.originalTransformation = selem.matrix ? selem.matrix : MatrixUtil.identityMatrix();
            }
            d.selectElems = [e];
            selectionInfo.setCurrSelection(d, true);
            r = true;
        } else {
            r = selectionInfo.hasSelection();
            selectionInfo.deselectAll();
        }
    }
    return r;
}